<template>
    <div class="d-flex justify-content-center py-3">
        <div class="card shadow-sm w-100 official-message-card">
            <div class="card-body">
                <h4 class="mb-3 text-center">Edit Official Message</h4>

                <form @submit.prevent="submitForm" class="d-flex flex-column gap-3">
                    <div>
                        <label for="subject" class="form-label">Subject</label>
                        <input
                                v-model="subject"
                                id="subject"
                                type="text"
                                class="form-control"
                                placeholder="Enter subject"
                                required
                                :disabled="loading"
                        >
                    </div>

                    <div class="form-group">
                        <label for="message" class="form-label">Message</label>
                        <ckeditor
                            v-model="message"
                            :editor="editor"
                            :config="editorConfig"
                        ></ckeditor>
                    </div>

                    <div>
                        <label for="files" class="form-label">New attachments</label>
                        <input
                                id="files"
                                ref="filesInput"
                                type="file"
                                class="form-control"
                                multiple
                                @change="handleFilesChange"
                                :disabled="loading"
                        >
                        <div class="mt-2">
                            <button
                                    type="button"
                                    class="btn btn-outline-secondary btn-sm"
                                    @click="clearFiles"
                                    :disabled="!selectedFiles.length || loading"
                            >
                                Clear files
                            </button>
                        </div>
                        <div v-if="selectedFiles.length" class="mt-2 d-flex flex-column gap-1">
                            <small class="text-muted">Selected files:</small>
                            <small v-for="(file, index) in selectedFiles" :key="`${file.name}-${index}`" class="text-break">
                                {{ file.name }}
                            </small>
                        </div>
                    </div>

                    <div v-if="attachments.length">
                        <label class="form-label">Current attachments</label>
                        <div class="d-flex flex-column gap-2 border rounded p-2">
                            <label
                                    v-for="attachment in attachments"
                                    :key="attachment.id"
                                    class="form-check d-flex align-items-center gap-2 mb-0"
                            >
                                <input
                                        v-model="attachmentsToDeleteIds"
                                        class="form-check-input"
                                        type="checkbox"
                                        :value="attachment.id"
                                        :disabled="loading"
                                >
                                <span class="form-check-label text-break">
                                    {{ attachment.original_name || attachment.name }}
                                    <span class="text-muted">({{ attachment.type }})</span>
                                </span>
                            </label>
                        </div>
                    </div>

                    <div v-if="errorMessage" class="alert alert-danger mb-0">
                        {{ errorMessage }}
                    </div>

                    <div v-if="successMessage" class="alert alert-success mb-0">
                        {{ successMessage }}
                    </div>

                    <div class="d-flex justify-content-end gap-2">
                        <router-link type="button" class="btn btn-secondary" :to="{ name: 'OfficialMessages' }">Cancel</router-link>
                        <button type="submit" class="btn btn-primary" :disabled="loading">
                            {{ loading ? 'Saving...' : 'Save Changes' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { Ckeditor } from '@ckeditor/ckeditor5-vue';
import {
    ClassicEditor,
    Essentials,
    Paragraph,
    Heading,
    Bold,
    Italic,
    Link,
    List,
    Indent,
    IndentBlock,
    Image,
    ImageUpload,
    SimpleUploadAdapter,
    MediaEmbed,
    ImageResize,
    ImageStyle,
    ImageToolbar,
    ImageBlock
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';

export default {
    components: {
        Ckeditor,
    },
    data() {
        return {
            subject: '',
            message: '',
            selectedFiles: [],
            attachments: [],
            attachmentsToDeleteIds: [],
            loading: false,
            errorMessage: '',
            successMessage: '',
            editor: ClassicEditor,
        };
    },

    computed: {
        editorConfig() {
            const baseUrl = (axios.defaults.baseURL || '').replace(/\/$/, '');
            const token = this.$store?.state?.auth?.token || this.$store?.state?.token || localStorage.getItem('token') || '';
            const headers = {
                Accept: 'application/json',
            };

            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }

            return {
                licenseKey: 'GPL',
                plugins: [
                    Essentials,
                    Paragraph,
                    Heading,
                    Bold,
                    Italic,
                    Link,
                    List,
                    Indent,
                    IndentBlock,
                    Image,
                    ImageUpload,
                    SimpleUploadAdapter,
                    MediaEmbed,
                    ImageResize,
                    ImageStyle,
                    ImageToolbar,
                    ImageBlock
                ],
                toolbar: [
                    'undo',
                    'redo',
                    '|',
                    'heading', 'bold', 'italic', 'link',
                    '|',
                    'bulletedList', 'numberedList', 'outdent', 'indent',
                    '|',
                    'imageUpload',
                    'mediaEmbed',
                ],
                mediaEmbed: {
                    previewsInData: true,
                },
                simpleUpload: {
                    uploadUrl: `${baseUrl}/admin/conversations/messages/official/image`,
                    headers,
                    withCredentials: true,
                },
                image: {
                    toolbar: [
                        'imageStyle:alignBlockLeft',
                        'imageStyle:block',
                        'imageStyle:alignBlockRight',
                        '|',
                        'imageResize',
                    ],
                }
            };
        },
    },

    mounted() {
        this.fetchMessage();
    },

    methods: {
        fetchMessage() {
            this.loading = true;
            axios.get(`admin/conversations/messages/official/${this.$route.params.id}`)
                .then(response => {
                    const data = response.data?.data ?? response.data;
                    this.subject = data.subject ?? '';
                    this.message = data.message ?? '';
                    this.attachments = Array.isArray(data.attachments) ? data.attachments : [];
                    this.attachmentsToDeleteIds = [];
                })
                .catch(() => {
                    this.errorMessage = 'Unable to load the message.';
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        handleFilesChange(event) {
            this.selectedFiles = Array.from(event.target.files || []);
        },

        clearFiles() {
            this.selectedFiles = [];
            if (this.$refs.filesInput) {
                this.$refs.filesInput.value = '';
            }
        },

        async submitForm() {
            this.errorMessage = '';
            this.successMessage = '';

            const subject = this.subject.trim();
            if (!subject) {
                this.errorMessage = 'Please enter a subject.';
                return;
            }

            const message = this.message.trim();
            if (!message) {
                this.errorMessage = 'Please enter a message.';
                return;
            }

            this.loading = true;

            try {
                const formData = new FormData();
                formData.append('subject', subject);
                formData.append('message', message);
                this.selectedFiles.forEach((file) => {
                    formData.append('files[]', file);
                });
                this.attachmentsToDeleteIds.forEach((id) => {
                    formData.append('deleted_attachment_ids[]', id);
                });

                await axios.put(`admin/conversations/messages/official/${this.$route.params.id}`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    transformRequest: [(data) => data],
                });

                this.successMessage = 'Message updated successfully.';
                this.attachments = this.attachments.filter((attachment) => !this.attachmentsToDeleteIds.includes(attachment.id));
                this.attachmentsToDeleteIds = [];
                this.clearFiles();
            } catch (error) {
                console.error('Error updating official message:', error);
                this.errorMessage = 'Unable to save changes.';
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.official-message-card {
    max-width: 920px;
}
.subject-input {
    min-height: 44px;
}

:deep(.ck-editor__editable_inline) {
    min-height: 220px;
}
</style>